/**
 * The friendly pieces the Human Resources screens share: a person's photo
 * (or their initials), a person card that opens their file, the big action
 * buttons, status chips, the preview bar, and printing or saving a page.
 */
import { useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../design/components";
import type { HrEmployee } from "../../domain/hr";
import type { RecordStatus } from "../../domain/hrFile";
import { locale, t } from "../../i18n";
import { saveFile } from "../../lib/saveFile";
import { setViewAs, useHrAccess } from "./access";
import h from "./Hr.module.css";

export { h };

const PALETTE = ["#4c9be8", "#46b96b", "#e08a3c", "#b07ce8", "#3cc4c4", "#e0524e", "#c9a227", "#5f7fe8"];

export function initials(name: string): string {
  const parts = name
    .replace(/\b(Al|Bin|Ibn)-?\s*/gi, "")
    .split(/\s+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export function Avatar({ name, photo, size = 44 }: { name: string; photo?: string | null; size?: number }) {
  const style: CSSProperties = { width: size, height: size, fontSize: size * 0.36 };
  if (photo) return <img className={h.avatar} src={photo} alt={name} style={style} />;
  const colour = PALETTE[[...name].reduce((s, c) => s + c.charCodeAt(0), 0) % PALETTE.length];
  return (
    <span className={h.avatar} style={{ ...style, background: colour }} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export const fileLink = (id: string, tab?: string) => `/hr-people/${encodeURIComponent(id)}${tab ? `?tab=${tab}` : ""}`;

/** A person, clickable to their file. */
export function PersonCard({
  e,
  sub,
  extra,
}: {
  e: Pick<HrEmployee, "id" | "name" | "jobTitle" | "department">;
  sub?: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <Link className={h.person} to={fileLink(e.id)} data-testid="hr-person-card">
      <Avatar name={e.name} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <div className={h.personName}>{e.name}</div>
        <div className={h.personSub}>{sub ?? [e.jobTitle, e.department].filter(Boolean).join(" · ")}</div>
      </span>
      {extra}
    </Link>
  );
}

/** A person's name that opens their file. */
export function PersonLink({ id, name }: { id: string; name: string }) {
  return (
    <Link to={fileLink(id)} data-testid="hr-person-link">
      {name}
    </Link>
  );
}

export function Action({
  icon,
  label,
  sub,
  to,
  onClick,
  tint,
  count,
  testId,
}: {
  icon: string;
  label: string;
  sub?: string;
  to?: string;
  onClick?: () => void;
  tint?: string;
  count?: number;
  testId?: string;
}) {
  const body = (
    <>
      <span className={h.actionIcon} style={{ ["--tint" as string]: tint }} aria-hidden="true">
        {icon}
      </span>
      <span>
        {t(label)}
        {sub && <small>{t(sub)}</small>}
      </span>
      {count ? <span className={h.badge}>{count}</span> : null}
    </>
  );
  return to ? (
    <Link className={h.action} to={to} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <button type="button" className={h.action} onClick={onClick} data-testid={testId}>
      {body}
    </button>
  );
}

export function Chip({ children, colour }: { children: ReactNode; colour?: string }) {
  return (
    <span className={h.chip} style={{ ["--c" as string]: colour }}>
      {children}
    </span>
  );
}

export const STATUS_COLOUR: Record<RecordStatus, string> = {
  requested: "var(--amber)",
  approved: "var(--green)",
  issued: "var(--green)",
  rejected: "var(--red)",
  cancelled: "var(--dim)",
  recorded: "var(--blue)",
  open: "var(--amber)",
  closed: "var(--dim)",
};

export const STATUS_WORD: Record<RecordStatus, string> = {
  requested: "Waiting for a decision",
  approved: "Approved",
  issued: "Issued",
  rejected: "Rejected",
  cancelled: "Canceled",
  recorded: "Recorded",
  open: "Open",
  closed: "Closed",
};

export function StatusChip({ status }: { status: RecordStatus }) {
  return <Chip colour={STATUS_COLOUR[status]}>{t(STATUS_WORD[status])}</Chip>;
}

/** Shown on every HR screen while somebody previews a lower level. */
export function PreviewBar() {
  const a = useHrAccess();
  if (!a.previewing) return null;
  return (
    <div className={h.preview} data-testid="hr-preview-bar">
      <span>{t("You are seeing Human Resources as {level} would.", { level: t(a.level.name) })}</span>
      <Button size="sm" onClick={() => setViewAs(null)}>
        {t("Back to my own view")}
      </Button>
    </div>
  );
}

/** A page of HTML, printed from a hidden frame; saved as a file when printing is not possible. */
export async function printHtml(title: string, bodyHtml: string): Promise<void> {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>
body{font-family:Georgia,'Times New Roman',serif;color:#111;margin:40px;line-height:1.6;font-size:15px}
h1{font-size:19px;text-align:center;text-decoration:underline}
table{border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:12px}
th,td{border:1px solid #bbb;padding:5px 7px;text-align:start}th{background:#eef2f6}
.head{display:flex;justify-content:space-between;border-bottom:2px solid #1b62ac;padding-bottom:6px;font-size:13px;color:#444}
</style></head><body>${bodyHtml}</body></html>`;
  try {
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
    document.body.appendChild(frame);
    const doc = frame.contentWindow?.document;
    if (!doc || !frame.contentWindow) throw new Error("no frame");
    doc.open();
    doc.write(html);
    doc.close();
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 2000);
  } catch {
    await saveFile(`${title.replace(/[^\w-]+/g, "-")}.html`, new Blob([html], { type: "text/html" }));
  }
}

export async function downloadHtml(title: string, bodyHtml: string): Promise<void> {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title></head><body>${bodyHtml}</body></html>`;
  await saveFile(`${title.replace(/[^\w-]+/g, "-")}.html`, new Blob([html], { type: "text/html" }));
}

export const escapeHtml = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);

/** A photo made small enough to keep: the longest side 320 px, JPEG. */
export function shrinkPhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(t("The photo could not be read.")));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error(t("That file is not a picture.")));
      img.onload = () => {
        const scale = Math.min(1, 320 / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

export const todayIso = () => new Date().toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) =>
  new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);
export const niceDate = (iso: string | null | undefined) =>
  iso
    ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(locale(), {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

/* ---- hourly rates: shown only when asked for ----------------------------------- */

/*
 * Everyone is paid a monthly salary, so the screens lead with the month. The
 * hourly figure — the month spread over the hours a person actually works —
 * is what prices a work order; it stays one switch away for whoever wants it.
 */
const HOURLY = "sky.hrHourly";
const hourlyListeners = new Set<() => void>();
function readHourly(): boolean {
  try {
    return localStorage.getItem(HOURLY) === "1";
  } catch {
    return false;
  }
}
export function setShowHourly(on: boolean) {
  try {
    if (on) localStorage.setItem(HOURLY, "1");
    else localStorage.removeItem(HOURLY);
  } catch {
    /* storage unavailable */
  }
  hourlyListeners.forEach((l) => l());
}
export function useShowHourly(): boolean {
  return useSyncExternalStore(
    (l) => {
      hourlyListeners.add(l);
      return () => hourlyListeners.delete(l);
    },
    readHourly,
    () => false,
  );
}

export function HourlySwitch() {
  const on = useShowHourly();
  return (
    <label
      style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13.5, cursor: "pointer" }}
      data-testid="hr-hourly-switch"
    >
      <input type="checkbox" checked={on} onChange={(e) => setShowHourly(e.target.checked)} />
      {t("Show hourly rates too")}
    </label>
  );
}
